import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { SECTIONS } from '../sections';
import { useFlagSearch } from '../FeatureFlagsContext';
import { usePhotoboothEnabled } from '../weddingCountdown';

const HEADER_HEIGHT_FALLBACK = 72;

function scrollToSection(id: string): void {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function SiteHeader() {
  const location = useLocation();
  const showPhotobooth = usePhotoboothEnabled();
  const flagSearch = useFlagSearch();
  const isHome = location.pathname === '/';
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(HEADER_HEIGHT_FALLBACK);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const sync = () => {
      const h = el.offsetHeight;
      setHeaderHeight(h);
      document.documentElement.style.setProperty('--header-height', `${h}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled) sync();
    });
    return () => {
      cancelled = true;
      ro.disconnect();
    };
  }, []);

  const updateActiveFromScroll = useCallback(() => {
    if (!isHome) return;
    const point = window.scrollY + headerHeight + 2;
    let active = SECTIONS[0].id;
    for (const { id } of SECTIONS) {
      const sectionEl = document.getElementById(id);
      if (!sectionEl) continue;
      const top = sectionEl.getBoundingClientRect().top + window.scrollY;
      if (top <= point) active = id;
    }
    setActiveSection(active);
  }, [headerHeight, isHome]);

  useEffect(() => {
    if (!isHome) return;
    updateActiveFromScroll();
    window.addEventListener('scroll', updateActiveFromScroll, { passive: true });
    return () => window.removeEventListener('scroll', updateActiveFromScroll);
  }, [updateActiveFromScroll, isHome]);

  return (
    <header className="site-header" ref={headerRef}>
      <div className="site-header__inner">
        <Link to={{ pathname: '/', search: flagSearch }} className="site-header__brand">
          V &amp; J-C
        </Link>
        <nav className="site-nav" aria-label="Navigation du site">
          <ul className="site-nav__list">
            {SECTIONS.map(({ id, label }) => (
              <li key={id}>
                {isHome ? (
                  <button
                    type="button"
                    className={`site-nav__tab${activeSection === id ? ' site-nav__tab--active' : ''}`}
                    onClick={() => scrollToSection(id)}
                    aria-current={activeSection === id ? 'true' : undefined}
                  >
                    {label}
                  </button>
                ) : (
                  <Link
                    to={{ pathname: '/', search: flagSearch }}
                    state={{ section: id }}
                    className="site-nav__tab site-nav__link"
                  >
                    {label}
                  </Link>
                )}
              </li>
            ))}
            {showPhotobooth && (
              <li>
                <NavLink
                  to={{ pathname: '/photos', search: flagSearch }}
                  className={({ isActive }) =>
                    `site-nav__tab${isActive ? ' site-nav__tab--active' : ''}`
                  }
                >
                  Photobooth
                </NavLink>
              </li>
            )}
            {!showPhotobooth && (
              <li>
                <button type="button" className="site-nav__tab site-nav__tab--disabled" disabled>
                  Patience...
                </button>
              </li>
            )}
          </ul>
        </nav>
      </div>
    </header>
  );
}
